import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useTheme } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import {
  Heart, Brain, BookOpen, Users, Briefcase, Target,
  Sparkles, Sun, Moon, Flame, Share2, Download, Tag, X, Volume2, VolumeX,
  ChevronDown, MoreVertical, Crown, Palette, Lock, Bookmark, Settings, Play
} from "lucide-react";
import { cn } from "@/lib/utils";
import InsightShareModal from "@/components/InsightShareModal";
import { SocialShareModal } from "@/components/SocialShareModal";
import { BackupAlert } from "@/components/BackupAlert";
import ConversationShareModal from "@/components/ConversationShareModal";
import { MicButton } from "@/components/MicButton";
import { VoiceSelector } from "@/components/VoiceSelector";
import { FreeTrialNotice } from "@/components/FreeTrialNotice";
import { useVoice } from "@/contexts/VoiceContext";
import { RecapModal } from "@/components/RecapModal";
import { MoodSparkline } from "@/components/MoodSparkline";
import { FeatureTour } from "@/components/FeatureTour";
import { AmbientSounds } from "@/components/AmbientSounds";
import { ShortcutCheatsheet } from "@/components/ShortcutCheatsheet";
import { TeamOnboarding } from "@/components/TeamOnboarding";
import { Tip } from "@/components/Tip";
import { ChatSummarizer } from "@/components/ChatSummarizer";
import { ThemeBadge, ThemeFilter, useConversationThemes } from "@/components/ConversationThemes";
import { ConversationInsights } from "@/components/ConversationInsights";
import { useVoiceShortcuts } from "@/hooks/useVoiceShortcuts";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";
import { useI18n } from "@/i18n/I18nProvider";
import { ChatSidebar } from "@/components/ChatSidebar";
import { ChatMessageList } from "@/components/ChatMessageList";
import { ChatInputArea } from "@/components/ChatInputArea";
import { WisdomCouncil, WisdomFigureBadge, RoundtableBadge } from "@/components/WisdomCouncil";
import { UpgradePrompt, UsageCounter } from "@/components/UpgradePrompt";
import { DailyRecap } from "@/components/DailyRecap";
import { SavedItemsModal } from "@/components/SavedItemsModal";
import { SmartGreetingBanner } from "@/components/SmartGreetingBanner";
import { MemoryPanel } from "@/components/MemoryPanel";
import { QuickSettingsPanel } from "@/components/QuickSettingsPanel";
import { ChatHeader, MODULES, EXCLUSIVE_MODULES } from "@/components/ChatHeader";
import { InsightNotifications } from "@/components/InsightNotifications";
import { ExportShareModal } from "@/components/ExportShareModal";
import { AIDisclosureNotice } from "@/components/ComplianceGates";


export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { t } = useI18n();
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [activeConv, setActiveConv] = useState(conversationId || null);
  const [module, setModule] = useState("friend");
  const [showSidebar, setShowSidebar] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showModules, setShowModules] = useState(false);
  const [shareMsg, setShareMsg] = useState(null);
  const [showConvShare, setShowConvShare] = useState(false);
  const [showExportShare, setShowExportShare] = useState(false);
  const [lastAiMsg, setLastAiMsg] = useState(null);
  const [reactionsMap, setReactionsMap] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [smartGreeting, setSmartGreeting] = useState(null);
  const [greetingDismissed, setGreetingDismissed] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [bookmarkedMsgs, setBookmarkedMsgs] = useState({});
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [categorizing, setCategorizing] = useState(false);
  const [folderMenuConv, setFolderMenuConv] = useState(null);
  const [showRecap, setShowRecap] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState({});
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [showTour, setShowTour] = useState(() => localStorage.getItem("omni_tour_done") !== "permanent" && !localStorage.getItem("omni_tour_done"));
  const [archivedConvs, setArchivedConvs] = useState([]);
  const [showArchive, setShowArchive] = useState(false);
  const [moodNoteInput, setMoodNoteInput] = useState("");
  const [moodNoteFor, setMoodNoteFor] = useState(null);
  const [resumeSession, setResumeSession] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [smartReplies, setSmartReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showTeamOnboarding, setShowTeamOnboarding] = useState(false);
  const [draftConvIds, setDraftConvIds] = useState(new Set());
  const [pinnedMsgs, setPinnedMsgs] = useState([]);
  const [showPinned, setShowPinned] = useState(false);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showNewTemplate, setShowNewTemplate] = useState(false);
  const [newTemplateText, setNewTemplateText] = useState("");
  const [streak, setStreak] = useState(0);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [themeFilter, setThemeFilter] = useState(null);
  const [draftSaveStatus, setDraftSaveStatus] = useState("idle"); // "idle" | "saving" | "saved"
  const [wisdomFigures, setWisdomFigures] = useState([]);
  const [showWisdomCouncil, setShowWisdomCouncil] = useState(false);
  const [activeWisdomFigure, setActiveWisdomFigure] = useState(null);
  const [roundtableFigures, setRoundtableFigures] = useState([]);
  const [roundtableMode, setRoundtableMode] = useState(false);
  const [showModuleMenu, setShowModuleMenu] = useState(false);
  const [showConvActions, setShowConvActions] = useState(false);
  const moduleMenuRef = useRef(null);
  const convActionsRef = useRef(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState(new Set());
  const [merging, setMerging] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState(null);
  const [usageStatus, setUsageStatus] = useState(null);
  const [psychologistEnabled, setPsychologistEnabled] = useState(true);
  const [showRecapCard, setShowRecapCard] = useState(true);
  const [showSavedItems, setShowSavedItems] = useState(false);
  const [showMemoryPanel, setShowMemoryPanel] = useState(false);
  const [showQuickSettings, setShowQuickSettings] = useState(false);
  const [lastConversation, setLastConversation] = useState(null);
  const [responseMode, setResponseMode] = useState(() => localStorage.getItem("omni_response_mode") || "concise");
  const { allThemes, collectThemes } = useConversationThemes();
  const { ttsEnabled, toggleTts, speaking, speakText, stopSpeaking, voice, currentVoiceInfo, ttsStatus, voiceOnboarded, dismissVoiceOnboarding } = useVoice();
  const messagesEnd = useRef(null);
  const inputRef = useRef(null);
  const searchRef = useRef(null);
  const startNewChatRef = useRef(null);
  const summarizeRef = useRef(null);
  const insightsRef = useRef(null);
  const inputRef2 = useRef(input);
  const headers = { Authorization: `Bearer ${token}` };
  const name = user?.persona?.preferred_name || user?.username || "Friend";
  const tier = user?.tier || "free";
  const freeModules = ["friend", "psychologist_basic"];

  // EU AI Act Art. 13 — transparency disclosure per persona/module
  const [aiAcks, setAiAcks] = useState(null);
  const [ackBusy, setAckBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/consent/status`, { headers })
      .then(({ data }) => setAiAcks(new Set(data.ai_disclosure_acks || [])))
      .catch(() => setAiAcks(new Set()));
  }, []);
  const ackAiModule = async () => {
    setAckBusy(true);
    try {
      await axios.post(`${API}/ai-disclosure/ack`, { module }, { headers });
    } catch (e) { console.error(e); }
    setAiAcks(prev => new Set([...(prev || []), module]));
    setAckBusy(false);
  };

  // Voice shortcuts
  const { processVoiceInput } = useVoiceShortcuts({
    onSummarize: () => setShowModuleMenu(v => !v),
    onSwitchModule: (mod) => setModule(mod),
    onNewChat: () => { setActiveConv(null); setMessages([]); navigate("/chat"); },
    onSearch: (q) => { handleSearch(q); },
    onReadLast: () => { if (lastAiMsg) speakText(lastAiMsg, "voice-cmd"); },
    onInsights: () => insightsRef.current?.click(),
    onToggleDark: () => toggleDarkMode(),
  });

  // Offline queue
  const handleOfflineSend = useCallback(async (item) => {
    const { data } = await axios.post(`${API}/chat/message`, {
      message: item.text,
      conversation_id: item.conversation_id,
      module: item.module
    }, { headers });
    if (data.companion_reminder) toast.info(data.companion_reminder, { duration: 9000 });
    const aiMsg = { role: "assistant", content: data.response, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, aiMsg]);
    setLastAiMsg(data.response);
    if (!item.conversation_id && data.conversation_id) {
      setActiveConv(data.conversation_id);
      navigate(`/chat/${data.conversation_id}`, { replace: true });
      fetchConversations();
    }
  }, [headers, module]);

  const { isOnline, queue: offlineQueue, flushing, enqueue: enqueueOffline, flushQueue } = useOfflineQueue({
    onSend: handleOfflineSend,
    onStatusChange: (status) => {
      if (status === "flushed") toast.success("Offline messages sent!");
    }
  });

  useEffect(() => {
    if (isOnline && offlineQueue.length > 0) flushQueue();
  }, [isOnline]);

  // Pinch-to-zoom
  const [zoomLevel, setZoomLevel] = useState(1);
  const messageAreaRef = useRef(null);

  useEffect(() => {
    const el = messageAreaRef.current;
    if (!el) return;
    let initialDist = 0;
    let initialZoom = 1;
    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };
    const onTouchStart = (e) => {
      if (e.touches.length === 2) { initialDist = getDistance(e.touches); initialZoom = zoomLevel; }
    };
    const onTouchMove = (e) => {
      if (e.touches.length === 2) {
        const dist = getDistance(e.touches);
        const scale = (dist / initialDist) * initialZoom;
        setZoomLevel(Math.min(Math.max(scale, 1), 3));
      }
    };
    const onTouchEnd = () => { setZoomLevel(prev => prev < 1.1 ? 1 : prev); };
    let lastTap = 0;
    const onDoubleTap = () => { const now = Date.now(); if (now - lastTap < 300) setZoomLevel(1); lastTap = now; };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchend", onDoubleTap, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchend", onDoubleTap);
    };
  }, [zoomLevel]);

  // Init
  useEffect(() => {
    fetchConversations(); fetchFolders(); fetchArchived(); checkResumeSession(); fetchTags(); fetchCustomTemplates(); fetchStreak(); fetchWisdomFigures(); fetchUsageStatus();
    if (user?.tier === "team_dev") {
      const onboardingState = localStorage.getItem("omni_team_onboarding");
      if (onboardingState !== "permanent" && onboardingState !== "done") setShowTeamOnboarding(true);
    }
    scanDrafts();
    // Fetch smart greeting
    axios.get(`${API}/chat/smart-greeting`, { headers }).then(r => setSmartGreeting(r.data)).catch(() => {});
    // Feature gates (regulatory set-aside)
    axios.get(`${API}/features`).then(r => {
      const live = r.data.psychologist_enabled !== false;
      setPsychologistEnabled(live);
      if (!live) setModule(m => (m === "psychologist" || m === "psychologist_basic") ? "friend" : m);
    }).catch(() => {});
  }, []);

  useEffect(() => { inputRef2.current = input; }, [input]);

  useEffect(() => {
    if (activeConv) {
      fetchMessages(activeConv);
      fetchReactions(activeConv);
      fetchBookmarkedMsgs(activeConv);
      fetchConvTags(activeConv);
      fetchPinnedMessages(activeConv);
      const draft = localStorage.getItem(`omni_draft_${activeConv}`);
      if (draft) setInput(draft);
      localStorage.setItem("omni_active_conv", activeConv);
    } else {
      setBookmarkedMsgs({});
      const welcomeDraft = localStorage.getItem("omni_draft_welcome");
      if (welcomeDraft && !conversationId) setInput(welcomeDraft);
    }
  }, [activeConv]);

  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    // Auto-speak AI responses when TTS is enabled (default: on)
    // Wisdom figure voice is handled inline in sendMessage with the figure-specific voice
    // Regular chat TTS requires add-on; wisdom TTS is free with limits
    if (ttsEnabled && lastAiMsg && !speaking && !activeWisdomFigure && roundtableFigures.length === 0) {
      speakText(lastAiMsg, "auto-latest", null, "regular");
    }
  }, [lastAiMsg, ttsEnabled]);

  // When wisdom figure is selected, auto-enable their voice for the session
  useEffect(() => {
    if (activeWisdomFigure?.voice) {
      localStorage.setItem("omni_wisdom_voice_active", "true");
    } else {
      localStorage.removeItem("omni_wisdom_voice_active");
    }
  }, [activeWisdomFigure]);

  // Persist response mode
  const handleResponseModeChange = (mode) => {
    setResponseMode(mode);
    localStorage.setItem("omni_response_mode", mode);
  };

  // Click-outside handler for dropdowns (conv actions only — module menu uses backdrop overlay)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showConvActions && convActionsRef.current && !convActionsRef.current.contains(e.target)) {
        setShowConvActions(false);
      }
    };
    if (showConvActions) {
      document.addEventListener("pointerdown", handleClickOutside);
      return () => document.removeEventListener("pointerdown", handleClickOutside);
    }
  }, [showConvActions]);

  useEffect(() => {
    const checkReminder = async () => {
      try {
        const { data } = await axios.get(`${API}/mood/reminder-check`, { headers });
        if (data.show_reminder) toast.info(t("mood.reminderToast"), { duration: 10000, action: { label: t("nav.mood"), onClick: () => navigate("/mood") } });
      } catch { /* silent */ }
    };
    checkReminder();
  }, []);

  useEffect(() => {
    const handleKeyboard = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (!mod) return;
      if (e.key === "n" || e.key === "N") { e.preventDefault(); startNewChatRef.current?.(); }
      else if (e.key === "k" || e.key === "K") { e.preventDefault(); searchRef.current?.focus(); }
      else if (e.key === "d" || e.key === "D") { e.preventDefault(); toggleDarkMode(); }
      else if (e.key === "b" || e.key === "B") { e.preventDefault(); setBulkMode(m => !m); }
    };
    window.addEventListener("keydown", handleKeyboard);
    return () => window.removeEventListener("keydown", handleKeyboard);
  }, [toggleDarkMode]);

  // ===================== API FUNCTIONS =====================

  const fetchConversations = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/conversations`, { headers });
      setConversations(data.conversations);
      collectThemes(data.conversations);
      // Track last conversation for Resume button
      if (data.conversations?.length > 0) {
        setLastConversation(data.conversations[0]);
      }
    } catch (err) { console.error(err); }
  };

  const fetchMessages = async (convId) => {
    try {
      const { data } = await axios.get(`${API}/chat/conversation/${convId}`, { headers });
      const msgs = data.messages || [];
      setMessages(msgs);
      setModule(data.module || "friend");
      if (data.wisdom_figure) setActiveWisdomFigure(wisdomFigures.find(f => f.key === data.wisdom_figure) || null);
      else setActiveWisdomFigure(null);
      const lastAi = [...msgs].reverse().find(m => m.role === "assistant");
      if (lastAi) setLastAiMsg(lastAi.content);
    } catch (err) { console.error(err); setMessages([]); }
  };

  const fetchWisdomFigures = async () => {
    try {
      const { data } = await axios.get(`${API}/wisdom-figures`);
      setWisdomFigures(data.figures || []);
    } catch (err) { console.error("Failed to load wisdom figures:", err); }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput(""); setSending(true);
    if (activeConv) localStorage.removeItem(`omni_draft_${activeConv}`);
    else localStorage.removeItem("omni_draft_welcome");
    const tempMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, tempMsg]);
    if (!isOnline) {
      enqueueOffline({ text, conversation_id: activeConv, module });
      const offlineNote = { role: "assistant", content: "You're offline. This message will be sent when you reconnect.", timestamp: new Date().toISOString(), offline: true };
      setMessages(prev => [...prev, offlineNote]);
      setSending(false);
      toast.info("Message queued \u2014 will send when you're back online");
      return;
    }

    // Roundtable mode: send to multiple figures
    if (roundtableFigures.length > 1) {
      try {
        const { data } = await axios.post(`${API}/chat/council-roundtable`, {
          message: text,
          conversation_id: activeConv,
          wisdom_figures: roundtableFigures.map(f => f.key),
          response_mode: responseMode,
        }, { headers });

        // Add each figure's response as a separate message
        for (const resp of data.responses) {
          const aiMsg = {
            role: "assistant",
            content: resp.response,
            timestamp: new Date().toISOString(),
            wisdom_voice: resp.voice || null,
            wisdom_figure_name: resp.figure_name,
            wisdom_figure_key: resp.figure_key,
            isRoundtable: true,
          };
          setMessages(prev => [...prev, aiMsg]);
        }
        setLastAiMsg(data.responses[data.responses.length - 1]?.response || "");
        // Auto-speak last roundtable response if TTS enabled (wisdom context)
        if (ttsEnabled && data.responses.length > 0) {
          const lastResp = data.responses[data.responses.length - 1];
          speakText(lastResp.response, "auto-latest", lastResp.voice || null, "wisdom");
        }
        if (!activeConv) {
          setActiveConv(data.conversation_id);
          navigate(`/chat/${data.conversation_id}`, { replace: true });
          fetchConversations();
        }
      } catch (err) {
        if (err.response?.status === 429 || err.response?.status === 402) {
          const detail = err.response?.data?.detail;
          if (typeof detail === "object" && detail?.type) {
            setUpgradePrompt(detail);
          } else {
            setUpgradePrompt({ type: "message_limit", message: typeof detail === "string" ? detail : "Daily limit reached.", tier, tier_name: "your plan" });
          }
        } else {
          toast.error(err.response?.data?.detail || "Failed to send roundtable message");
        }
        setMessages(prev => prev.filter(m => m !== tempMsg));
      } finally { setSending(false); inputRef.current?.focus(); fetchUsageStatus(); }
      return;
    }

    try {
      const { data } = await axios.post(`${API}/chat/message`, { message: text, conversation_id: activeConv, module, wisdom_figure: activeWisdomFigure?.key || null, response_mode: responseMode }, { headers });
      const aiMsg = { role: "assistant", content: data.response, timestamp: new Date().toISOString(), wisdom_voice: data.wisdom_voice || null };
      setMessages(prev => [...prev, aiMsg]);
      setLastAiMsg(data.response);
      // Auto-speak with figure voice (always auto-speak for wisdom council)
      if (data.wisdom_voice) {
        speakText(data.response, "auto-latest", data.wisdom_voice, "wisdom");
      }
      fetchSmartReplies(data.response);
      if (!activeConv) {
        setActiveConv(data.conversation_id);
        navigate(`/chat/${data.conversation_id}`, { replace: true });
        fetchConversations();
      }
    } catch (err) {
      if (err.response?.status === 429) {
        const detail = err.response?.data?.detail;
        if (typeof detail === "object" && detail?.type) {
          setUpgradePrompt(detail);
        } else {
          setUpgradePrompt({ type: "message_limit", message: typeof detail === "string" ? detail : "Daily limit reached.", tier, tier_name: "your plan" });
        }
      } else {
        toast.error(err.response?.data?.detail || "Failed to send message");
      }
      setMessages(prev => prev.filter(m => m !== tempMsg));
    } finally { setSending(false); inputRef.current?.focus(); fetchUsageStatus(); }
  };

  const sendBulkMessages = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    const questions = text.split("\n").map(q => q.trim()).filter(q => q.length > 0);
    if (questions.length === 0) return;
    if (questions.length === 1) return sendMessage(e);
    setInput(""); setSending(true); setBulkProgress({ current: 0, total: questions.length });
    if (activeConv) localStorage.removeItem(`omni_draft_${activeConv}`);
    else localStorage.removeItem("omni_draft_welcome");
    let convId = activeConv; let isNewConv = false;
    for (let i = 0; i < questions.length; i++) {
      setBulkProgress({ current: i + 1, total: questions.length });
      const userMsg = { role: "user", content: questions[i], timestamp: new Date().toISOString() };
      setMessages(prev => [...prev, userMsg]);
      try {
        const { data } = await axios.post(`${API}/chat/message`, { message: questions[i], conversation_id: convId, module }, { headers });
        if (data.companion_reminder) toast.info(data.companion_reminder, { duration: 9000 });
        const aiMsg = { role: "assistant", content: data.response, timestamp: new Date().toISOString() };
        setMessages(prev => [...prev, aiMsg]);
        setLastAiMsg(data.response);
        if (!convId) { convId = data.conversation_id; isNewConv = true; }
      } catch (err) {
        if (err.response?.status === 429 || err.response?.status === 402) {
          const detail = err.response?.data?.detail;
          if (typeof detail === "object" && detail?.type) setUpgradePrompt(detail);
          else setUpgradePrompt({ type: "message_limit", message: typeof detail === "string" ? detail : "Daily limit reached.", tier, tier_name: "your plan" });
          break;
        }
        toast.error(err.response?.data?.detail || `Failed on question ${i + 1}`); break;
      }
    }
    if (isNewConv && convId) { setActiveConv(convId); navigate(`/chat/${convId}`, { replace: true }); fetchConversations(); }
    setSending(false); setBulkProgress(null);
    if (lastAiMsg) fetchSmartReplies(lastAiMsg);
    inputRef.current?.focus();
  };

  const startNewChat = () => {
    saveDraft();
    setActiveConv(null); setMessages([]); setSmartReplies([]);
    const welcomeDraft = localStorage.getItem("omni_draft_welcome");
    setInput(welcomeDraft || ""); setModule("friend"); navigate("/chat");
  };
  startNewChatRef.current = startNewChat;

  const saveDraft = useCallback(() => {
    const text = inputRef2.current;
    if (activeConv) {
      if (text && text.trim()) localStorage.setItem(`omni_draft_${activeConv}`, text);
      else localStorage.removeItem(`omni_draft_${activeConv}`);
    } else {
      if (text && text.trim()) localStorage.setItem("omni_draft_welcome", text);
      else localStorage.removeItem("omni_draft_welcome");
    }
    if (text && text.trim()) {
      setDraftSaveStatus("saved");
      setTimeout(() => setDraftSaveStatus("idle"), 2500);
    }
  }, [activeConv]);

  // Auto-save draft + visibility
  useEffect(() => {
    const interval = setInterval(saveDraft, 3000);
    const handleBeforeUnload = () => saveDraft();
    const handleVisibility = () => { if (document.visibilityState === "hidden") saveDraft(); };
    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => { clearInterval(interval); window.removeEventListener("beforeunload", handleBeforeUnload); document.removeEventListener("visibilitychange", handleVisibility); };
  }, [saveDraft]);

  // Backend session sync
  useEffect(() => {
    const collectDrafts = () => {
      const d = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith("omni_draft_")) d[key.replace("omni_draft_", "")] = localStorage.getItem(key);
      }
      return d;
    };
    const saveSessionToBackend = async () => {
      try { await axios.post(`${API}/sessions/save`, { conversation_id: activeConv || null, module, drafts: collectDrafts() }, { headers }); } catch {}
    };
    const interval = setInterval(saveSessionToBackend, 30000);
    const handleUnload = () => {
      navigator.sendBeacon?.(`${API}/sessions/save-beacon`, JSON.stringify({ token, conversation_id: activeConv || null, module, drafts: collectDrafts() }));
    };
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        try { navigator.sendBeacon?.(`${API}/sessions/save-beacon`, JSON.stringify({ token, conversation_id: activeConv || null, module, drafts: collectDrafts() })); } catch {}
      }
    };
    window.addEventListener("beforeunload", handleUnload);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => { clearInterval(interval); window.removeEventListener("beforeunload", handleUnload); document.removeEventListener("visibilitychange", handleVisibility); };
  }, [activeConv, module, token]);

  const scanDrafts = () => {
    const ids = new Set();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("omni_draft_") && key !== "omni_draft_welcome") {
        const val = localStorage.getItem(key);
        if (val && val.trim()) ids.add(key.replace("omni_draft_", ""));
      }
    }
    setDraftConvIds(ids);
  };

  useEffect(() => { scanDrafts(); }, [activeConv, input]);

  const fetchSmartReplies = async (aiMessage) => {
    if (!aiMessage || sending) return;
    setLoadingReplies(true);
    try {
      const { data } = await axios.post(`${API}/chat/suggestions`, { ai_message: aiMessage, module }, { headers });
      setSmartReplies(data.suggestions || []);
    } catch { setSmartReplies([]); }
    finally { setLoadingReplies(false); }
  };

  const sendSmartReply = (text) => {
    setInput(text); setSmartReplies([]);
    setTimeout(() => { inputRef.current?.focus(); }, 50);
  };

  useEffect(() => { localStorage.setItem("omni_active_module", module); }, [module]);

  const selectConversation = (convId) => {
    saveDraft(); setInput(""); setSmartReplies([]);
    setActiveConv(convId); navigate(`/chat/${convId}`); setShowSidebar(false);
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try { await axios.delete(`${API}/chat/conversation/${convId}`, { headers }); setConversations(prev => prev.filter(c => c.id !== convId)); if (activeConv === convId) startNewChat(); }
    catch { toast.error("Failed to delete"); }
  };

  const exportConversation = async (format = "pdf") => {
    if (!activeConv) return;
    try {
      const endpoint = format === "text" ? `${API}/chat/export/${activeConv}/text` : `${API}/chat/export/${activeConv}`;
      const response = await axios.get(endpoint, { headers, responseType: "blob" });
      const ext = format === "text" ? "txt" : "pdf";
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a"); a.href = url; a.download = `omni_session_${activeConv.slice(0, 8)}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${ext.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  // Tags
  const [allTags, setAllTags] = useState([]);
  const [convTags, setConvTags] = useState([]);
  const [showTagMenu, setShowTagMenu] = useState(false);
  const [tagFilter, setTagFilter] = useState(null);
  const [tagColors, setTagColors] = useState([]);

  const fetchTags = async () => { try { const { data } = await axios.get(`${API}/tags`, { headers }); setAllTags(data.tags || []); setTagColors(data.colors || []); } catch {} };
  const fetchConvTags = async (convId) => { try { const { data } = await axios.get(`${API}/conversations/${convId}/tags`, { headers }); setConvTags(data.tags || []); } catch { setConvTags([]); } };
  const createTag = async (label, color) => { try { await axios.post(`${API}/tags`, { label, color }, { headers }); fetchTags(); toast.success("Tag created"); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const toggleConvTag = async (tagId) => { if (!activeConv) return; const has = convTags.includes(tagId); try { await axios.post(`${API}/conversations/${activeConv}/tags`, { tag_id: tagId, action: has ? "remove" : "add" }, { headers }); setConvTags(prev => has ? prev.filter(t => t !== tagId) : [...prev, tagId]); } catch {} };
  const deleteTag = async (tagId) => { try { await axios.delete(`${API}/tags/${tagId}`, { headers }); setAllTags(prev => prev.filter(t => t.id !== tagId)); setConvTags(prev => prev.filter(t => t !== tagId)); } catch {} };

  // Pinned Messages
  const fetchPinnedMessages = async (convId) => { try { const { data } = await axios.get(`${API}/conversations/${convId}/pinned-messages`, { headers }); setPinnedMsgs(data.pinned_messages || []); } catch { setPinnedMsgs([]); } };
  const togglePinMessage = async (msgIndex) => { if (!activeConv) return; try { await axios.post(`${API}/conversations/${activeConv}/pin-message`, { msg_index: msgIndex, action: pinnedMsgs.some(p => p.index === msgIndex) ? "unpin" : "pin" }, { headers }); fetchPinnedMessages(activeConv); } catch {} };

  // Custom Templates
  const fetchCustomTemplates = async () => { try { const { data } = await axios.get(`${API}/templates`, { headers }); setCustomTemplates(data.templates || []); } catch {} };
  const saveTemplate = async () => { if (!newTemplateText.trim()) return; try { await axios.post(`${API}/templates`, { text: newTemplateText.trim(), module }, { headers }); setNewTemplateText(""); setShowNewTemplate(false); fetchCustomTemplates(); toast.success("Template saved"); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } };
  const deleteTemplate = async (id) => { try { await axios.delete(`${API}/templates/${id}`, { headers }); setCustomTemplates(prev => prev.filter(t => t.id !== id)); } catch {} };

  // Streak
  const fetchStreak = async () => { try { const { data } = await axios.get(`${API}/streak`, { headers }); setStreak(data.streak || 0); } catch {} };

  const fetchReactions = async (convId) => { try { const { data } = await axios.get(`${API}/chat/reactions/${convId}`, { headers }); setReactionsMap(data.reactions || {}); } catch { setReactionsMap({}); } };
  const handleReactionChange = (msgIdx, newReactions) => {
    setReactionsMap(prev => { const next = { ...prev }; if (Object.keys(newReactions).length === 0) delete next[String(msgIdx)]; else next[String(msgIdx)] = newReactions; return next; });
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query || query.length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try { const { data } = await axios.get(`${API}/chat/search?q=${encodeURIComponent(query)}`, { headers }); setSearchResults(data.results); }
    catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const handleVoiceTranscript = (text) => {
    const wasShortcut = processVoiceInput(text);
    if (wasShortcut) return;
    setInput(prev => prev ? prev + " " + text : text);
    inputRef.current?.focus();
  };

  const handleQuestionResponderSubmit = (structuredMessage) => {
    setInput(structuredMessage);
    setTimeout(() => { document.querySelector('form')?.requestSubmit(); }, 50);
  };

  const fetchBookmarkedMsgs = async (convId) => { try { const { data } = await axios.get(`${API}/bookmarks/check/${convId}`, { headers }); setBookmarkedMsgs(data.bookmarks || {}); } catch { setBookmarkedMsgs({}); } };
  const togglePin = async (convId, e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.post(`${API}/chat/pin/${convId}`, {}, { headers });
      setConversations(prev => {
        const updated = prev.map(c => c.id === convId ? { ...c, pinned: data.pinned, pinned_at: data.pinned ? new Date().toISOString() : null } : c);
        return [...updated.filter(c => c.pinned).sort((a, b) => (b.pinned_at || "").localeCompare(a.pinned_at || "")), ...updated.filter(c => !c.pinned)];
      });
      toast.success(data.pinned ? "Conversation pinned" : "Conversation unpinned");
    } catch { toast.error("Failed to toggle pin"); }
  };
  const toggleBookmark = async (msgIndex) => {
    if (!activeConv) return;
    try {
      const { data } = await axios.post(`${API}/bookmarks`, { conversation_id: activeConv, message_index: msgIndex }, { headers });
      setBookmarkedMsgs(prev => { const next = { ...prev }; if (data.bookmarked) next[String(msgIndex)] = data.id; else delete next[String(msgIndex)]; return next; });
      toast.success(data.bookmarked ? "Message bookmarked" : "Bookmark removed");
    } catch { toast.error("Failed to toggle bookmark"); }
  };

  // Folders
  const fetchFolders = async () => { try { const { data } = await axios.get(`${API}/folders`, { headers }); setFolders(data.folders || []); } catch {} };
  const createFolder = async () => { const name = newFolderName.trim(); if (!name) return; try { const { data } = await axios.post(`${API}/folders`, { name }, { headers }); setFolders(prev => [...prev, { ...data, count: 0 }]); setNewFolderName(""); setShowNewFolder(false); toast.success("Folder created"); } catch (err) { toast.error(err.response?.data?.detail || "Failed to create folder"); } };
  const deleteFolder = async (folderId, e) => { e.stopPropagation(); try { await axios.delete(`${API}/folders/${folderId}`, { headers }); setFolders(prev => prev.filter(f => f.id !== folderId)); if (activeFolder === folderId) setActiveFolder(null); fetchConversations(); toast.success("Folder deleted"); } catch { toast.error("Failed to delete folder"); } };
  const assignFolder = async (convId, folderId) => { try { await axios.patch(`${API}/chat/conversation/${convId}/folder`, { folder_id: folderId }, { headers }); setConversations(prev => prev.map(c => c.id === convId ? { ...c, folder_id: folderId } : c)); setFolderMenuConv(null); fetchFolders(); toast.success(folderId ? "Moved to folder" : "Moved to uncategorized"); } catch { toast.error("Failed to move conversation"); } };
  const autoCategorize = async () => { setCategorizing(true); try { const { data } = await axios.post(`${API}/folders/auto-categorize`, {}, { headers }); if (data.categorized > 0) { toast.success(`Organized ${data.categorized} conversation${data.categorized > 1 ? "s" : ""}`); fetchConversations(); fetchFolders(); } else toast.info("No conversations to categorize"); } catch { toast.error("Auto-categorize failed"); } finally { setCategorizing(false); } };
  const startRenameFolder = (f, e) => { e.stopPropagation(); setEditingFolder(f.id); setEditFolderName(f.name); };
  const renameFolder = async (folderId) => { const name = editFolderName.trim(); if (!name) { setEditingFolder(null); return; } try { await axios.put(`${API}/folders/${folderId}`, { name }, { headers }); setFolders(prev => prev.map(f => f.id === folderId ? { ...f, name } : f)); toast.success("Folder renamed"); } catch { toast.error("Failed to rename"); } setEditingFolder(null); };

  const applyTemplate = (tpl) => { setModule(tpl.module); setInput(tpl.text); inputRef.current?.focus(); };

  const filteredConversations = (activeFolder === null
    ? conversations
    : activeFolder === "uncategorized"
      ? conversations.filter(c => !c.folder_id)
      : conversations.filter(c => c.folder_id === activeFolder)
  ).filter(c => !tagFilter || (c.tags || []).includes(tagFilter))
   .filter(c => !themeFilter || (c.themes || []).includes(themeFilter));

  // Archive
  const fetchArchived = async () => { try { const { data } = await axios.get(`${API}/chat/archived`, { headers }); setArchivedConvs(data.conversations || []); } catch {} };
  const archiveConversation = async (convId, e) => { e.stopPropagation(); try { await axios.post(`${API}/chat/archive/${convId}`, {}, { headers }); setConversations(prev => prev.filter(c => c.id !== convId)); if (activeConv === convId) startNewChat(); fetchArchived(); toast.success("Conversation archived"); } catch { toast.error("Failed to archive"); } };
  const unarchiveConversation = async (convId) => { try { await axios.post(`${API}/chat/archive/${convId}`, {}, { headers }); setArchivedConvs(prev => prev.filter(c => c.id !== convId)); fetchConversations(); toast.success("Conversation restored"); } catch { toast.error("Failed to restore"); } };

  // Merge conversations
  const toggleMergeSelect = (convId) => {
    setMergeSelection(prev => {
      const next = new Set(prev);
      if (next.has(convId)) next.delete(convId);
      else next.add(convId);
      return next;
    });
  };
  const cancelMerge = () => { setMergeMode(false); setMergeSelection(new Set()); };
  const executeMerge = async () => {
    if (mergeSelection.size < 2) { toast.error("Select at least 2 conversations"); return; }
    setMerging(true);
    try {
      const { data } = await axios.post(`${API}/chat/conversations/merge`, { conversation_ids: Array.from(mergeSelection) }, { headers });
      toast.success(`Merged ${data.merged_count} conversations (${data.message_count} messages)`);
      setMergeMode(false); setMergeSelection(new Set());
      fetchConversations();
      selectConversation(data.conversation_id);
    } catch (err) { toast.error(err.response?.data?.detail || "Merge failed"); }
    finally { setMerging(false); }
  };

  // Usage status (remaining messages counter)
  const fetchUsageStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/chat/usage-status`, { headers });
      setUsageStatus(data);
    } catch { /* silent */ }
  };

  // Mood
  const quickMoodLog = async (mood) => { setMoodNoteFor(mood); setMoodNoteInput(""); };
  const submitMoodWithNote = async () => { if (!moodNoteFor) return; try { await axios.post(`${API}/mood/log`, { score: moodNoteFor.score, emotions: moodNoteFor.emotions, notes: moodNoteInput.trim() || `Quick check-in: ${moodNoteFor.label}` }, { headers }); toast.success(`Mood logged: ${moodNoteFor.emoji} ${moodNoteFor.label}`); } catch { toast.error("Failed to log mood"); } setMoodNoteFor(null); setMoodNoteInput(""); };
  const skipMoodNote = async () => { if (!moodNoteFor) return; try { await axios.post(`${API}/mood/log`, { score: moodNoteFor.score, emotions: moodNoteFor.emotions, notes: `Quick check-in: ${moodNoteFor.label}` }, { headers }); toast.success(`Mood logged: ${moodNoteFor.emoji} ${moodNoteFor.label}`); } catch { toast.error("Failed to log mood"); } setMoodNoteFor(null); setMoodNoteInput(""); };

  // Session Resume
  const checkResumeSession = async () => { try { const { data } = await axios.get(`${API}/sessions`, { headers }); const sessions = data.sessions || []; if (sessions.length > 0 && !conversationId) { const latest = sessions[0]; if (latest.conversation_id || latest.draft_text) setResumeSession(latest); } } catch {} };
  const resumeLastSession = () => {
    if (!resumeSession) return;
    if (resumeSession.conversation_id) { selectConversation(resumeSession.conversation_id); if (resumeSession.draft_text) setTimeout(() => setInput(resumeSession.draft_text), 500); }
    else if (resumeSession.draft_text) { setInput(resumeSession.draft_text); localStorage.setItem("omni_draft_welcome", resumeSession.draft_text); }
    if (resumeSession.module) setModule(resumeSession.module);
    setResumeSession(null);
  };
  const dismissResume = () => setResumeSession(null);

  const [wakeTriggered, setWakeTriggered] = useState(false);
  const handleWakeWord = () => { setWakeTriggered(true); setTimeout(() => setWakeTriggered(false), 100); };

  // ===================== RENDER =====================

  return (
    <AppLayout>
      <div className="flex-1 flex h-full min-w-0 overflow-hidden">
        {/* Sidebar */}
        <ChatSidebar
          t={t}
          conversations={conversations} activeConv={activeConv} draftConvIds={draftConvIds}
          allTags={allTags} tagColors={tagColors} tagFilter={tagFilter} themeFilter={themeFilter} allThemes={allThemes}
          folders={folders} activeFolder={activeFolder} expandedFolders={expandedFolders}
          editingFolder={editingFolder} editFolderName={editFolderName}
          showNewFolder={showNewFolder} newFolderName={newFolderName}
          categorizing={categorizing} folderMenuConv={folderMenuConv}
          archivedConvs={archivedConvs} showArchive={showArchive}
          searchQuery={searchQuery} searchResults={searchResults} searching={searching}
          searchRef={searchRef} filteredConversations={filteredConversations}
          selectConversation={selectConversation} startNewChat={startNewChat}
          deleteConversation={deleteConversation} togglePin={togglePin}
          archiveConversation={archiveConversation} unarchiveConversation={unarchiveConversation}
          handleSearch={handleSearch} createFolder={createFolder} deleteFolder={deleteFolder}
          assignFolder={assignFolder} autoCategorize={autoCategorize}
          startRenameFolder={startRenameFolder} renameFolder={renameFolder}
          setTagFilter={setTagFilter} setThemeFilter={setThemeFilter} setActiveFolder={setActiveFolder}
          setFolderMenuConv={setFolderMenuConv} setShowNewFolder={setShowNewFolder} setNewFolderName={setNewFolderName}
          setEditingFolder={setEditingFolder} setEditFolderName={setEditFolderName}
          setShowArchive={setShowArchive} setSearchQuery={setSearchQuery} setSearchResults={setSearchResults}
          setActiveConv={setActiveConv}
          mergeMode={mergeMode} setMergeMode={setMergeMode}
          mergeSelection={mergeSelection} toggleMergeSelect={toggleMergeSelect}
          executeMerge={executeMerge} cancelMerge={cancelMerge} merging={merging}
          mobileOpen={mobileOpen} setMobileOpen={setMobileOpen}
        />

        {/* AI Act Art. 13 transparency disclosure */}
        {aiAcks && !aiAcks.has(module) && (
          <AIDisclosureNotice module={module} onAck={ackAiModule} busy={ackBusy} />
        )}

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Compact Header */}
          <ChatHeader
            module={module} setModule={setModule}
            showModuleMenu={showModuleMenu} setShowModuleMenu={setShowModuleMenu}
            activeWisdomFigure={activeWisdomFigure} setActiveWisdomFigure={setActiveWisdomFigure}
            roundtableFigures={roundtableFigures} setRoundtableFigures={setRoundtableFigures}
            setRoundtableMode={setRoundtableMode} setShowWisdomCouncil={setShowWisdomCouncil}
            ttsEnabled={ttsEnabled} toggleTts={toggleTts} speaking={speaking} stopSpeaking={stopSpeaking} ttsStatus={ttsStatus}
            usageStatus={usageStatus} streak={streak}
            psychologistEnabled={psychologistEnabled}
            setShowSavedItems={setShowSavedItems} setShowMemoryPanel={setShowMemoryPanel} setShowQuickSettings={setShowQuickSettings}
            darkMode={darkMode} toggleDarkMode={toggleDarkMode}
            activeConv={activeConv} messages={messages}
            showConvActions={showConvActions} setShowConvActions={setShowConvActions}
            showTagMenu={showTagMenu} setShowTagMenu={setShowTagMenu}
            setShowConvShare={setShowConvShare} setShowExportShare={setShowExportShare} exportConversation={exportConversation}
            allTags={allTags} convTags={convTags} tagColors={tagColors}
            toggleConvTag={toggleConvTag} createTag={createTag} deleteTag={deleteTag}
            user={user} tier={tier}
            moduleMenuRef={moduleMenuRef} convActionsRef={convActionsRef} insightsRef={insightsRef}
            t={t}
            setMobileOpen={setMobileOpen}
          />

          {/* Backup Alert */}
          {!activeConv && <BackupAlert />}

          {/* Resume Last Conversation Button */}
          {!activeConv && lastConversation && (
            <div className="px-4 pt-2">
              <button
                data-testid="resume-conversation-btn"
                onClick={() => {
                  setActiveConv(lastConversation.id);
                  navigate(`/chat/${lastConversation.id}`);
                  fetchMessages(lastConversation.id);
                }}
                className="w-full flex items-center gap-3 p-3.5 rounded-2xl border border-primary/20 bg-primary/[0.03] hover:bg-primary/[0.06] hover:border-primary/30 transition-all group"
              >
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/15 transition-colors flex-shrink-0">
                  <Play className="w-4 h-4 text-primary ml-0.5" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-xs font-semibold text-primary">Resume Last Conversation</p>
                  <p className="text-[10px] text-muted-foreground truncate">
                    {lastConversation.title || lastConversation.module || "Continue where you left off"}
                  </p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground -rotate-90 flex-shrink-0" />
              </button>
            </div>
          )}

          {/* Daily Recap Card */}
          {showRecapCard && !activeConv && (
            <DailyRecap token={token} onDismiss={() => setShowRecapCard(false)} />
          )}

          {/* Smart Cultural Greeting Banner */}
          {!activeConv && smartGreeting?.cultural && !greetingDismissed && (
            <SmartGreetingBanner
              greeting={smartGreeting.greeting}
              translation={smartGreeting.translation}
              followUp={smartGreeting.follow_up}
              valuesNote={smartGreeting.values_note}
              onDismiss={() => setGreetingDismissed(true)}
            />
          )}

          {!activeConv && (
            <div className="px-4 pt-1">
              <InsightNotifications />
            </div>
          )}

          {/* Messages */}
          <ChatMessageList
            t={t} name={name}
            messages={messages} activeConv={activeConv} module={module} sending={sending} bulkProgress={bulkProgress}
            resumeSession={resumeSession} messagesEndRef={messagesEnd} messageAreaRef={messageAreaRef}
            zoomLevel={zoomLevel} setZoomLevel={setZoomLevel}
            isOnline={isOnline} offlineQueue={offlineQueue} flushing={flushing}
            pinnedMsgs={pinnedMsgs} showPinned={showPinned} setShowPinned={setShowPinned}
            reactionsMap={reactionsMap} bookmarkedMsgs={bookmarkedMsgs}
            handleReactionChange={handleReactionChange} toggleBookmark={toggleBookmark} togglePinMessage={togglePinMessage}
            smartReplies={smartReplies} loadingReplies={loadingReplies} sendSmartReply={sendSmartReply}
            moodNoteFor={moodNoteFor} moodNoteInput={moodNoteInput} setMoodNoteInput={setMoodNoteInput}
            quickMoodLog={quickMoodLog} submitMoodWithNote={submitMoodWithNote} skipMoodNote={skipMoodNote}
            customTemplates={customTemplates} showNewTemplate={showNewTemplate} setShowNewTemplate={setShowNewTemplate}
            newTemplateText={newTemplateText} setNewTemplateText={setNewTemplateText}
            saveTemplate={saveTemplate} deleteTemplate={deleteTemplate} applyTemplate={applyTemplate} inputRef={inputRef}
            showVoiceOnboarding={!voiceOnboarded} setShowVoiceOnboarding={dismissVoiceOnboarding}
            resumeLastSession={resumeLastSession} dismissResume={dismissResume}
            setShareMsg={setShareMsg} setInput={setInput}
            draftConvIds={draftConvIds} conversations={conversations} selectConversation={selectConversation}
            onOpenWisdomCouncil={() => setShowWisdomCouncil(true)} activeWisdomFigure={activeWisdomFigure}
          />

          {/* Input Area */}
          <ChatInputArea
            t={t} module={module} messages={messages} sending={sending}
            bulkMode={bulkMode} setBulkMode={setBulkMode} bulkProgress={bulkProgress}
            input={input} setInput={setInput} inputRef={inputRef} saveDraft={saveDraft}
            sendMessage={sendMessage} sendBulkMessages={sendBulkMessages} handleWakeWord={handleWakeWord}
            ttsEnabled={ttsEnabled} toggleTts={toggleTts} speaking={speaking}
            voice={voice} currentVoiceInfo={currentVoiceInfo}
            setShowVoiceSelector={setShowVoiceSelector} handleVoiceTranscript={handleVoiceTranscript}
            lastAiMsg={lastAiMsg} activeConv={activeConv} handleQuestionResponderSubmit={handleQuestionResponderSubmit}
            moodNoteFor={moodNoteFor} moodNoteInput={moodNoteInput} setMoodNoteInput={setMoodNoteInput}
            quickMoodLog={quickMoodLog} submitMoodWithNote={submitMoodWithNote} skipMoodNote={skipMoodNote}
            draftSaveStatus={draftSaveStatus}
            responseMode={responseMode} setResponseMode={handleResponseModeChange}
          />
        </div>
      </div>

      {/* Modals */}
      {shareMsg && (
        <SocialShareModal
          content={shareMsg.content}
          module={shareMsg.module}
          wisdomFigure={shareMsg.wisdomFigure || (activeWisdomFigure?.name)}
          conversationTitle={conversations.find(c => c.id === activeConv)?.title}
          onClose={() => setShareMsg(null)}
        />
      )}
      {showConvShare && activeConv && <ConversationShareModal messages={messages} conversationId={activeConv} module={module} onClose={() => setShowConvShare(false)} />}
      {showExportShare && activeConv && <ExportShareModal conversationId={activeConv} onClose={() => setShowExportShare(false)} />}
      {showRecap && <RecapModal onClose={() => setShowRecap(false)} />}
      {showTour && !showRecap && <FeatureTour onComplete={() => setShowTour(false)} />}
      <SavedItemsModal open={showSavedItems} onClose={() => setShowSavedItems(false)} />
      <MemoryPanel open={showMemoryPanel} onClose={() => setShowMemoryPanel(false)} />
      <QuickSettingsPanel
        open={showQuickSettings}
        onClose={() => setShowQuickSettings(false)}
        onOpenMemory={() => setShowMemoryPanel(true)}
      />
      <ShortcutCheatsheet />
      {showTeamOnboarding && <TeamOnboarding onComplete={() => setShowTeamOnboarding(false)} />}
      <VoiceSelector open={showVoiceSelector} onClose={() => setShowVoiceSelector(false)} />
      <FreeTrialNotice />
      {upgradePrompt && <UpgradePrompt limitInfo={upgradePrompt} onClose={() => setUpgradePrompt(null)} />}

      {/* Wisdom Council Panel */}
      {showWisdomCouncil && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={() => setShowWisdomCouncil(false)}>
          <div
            className="w-full sm:w-[600px] md:w-[700px] h-[85vh] sm:h-[80vh] bg-background rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
            onClick={e => e.stopPropagation()}
          >
            <WisdomCouncil
              figures={wisdomFigures}
              multiSelect={roundtableMode}
              onSelect={(fig) => {
                setActiveWisdomFigure(fig);
                setRoundtableFigures([]);
                setShowWisdomCouncil(false);
                toast.success(`Consulting ${fig.name}`, { description: fig.greeting });
              }}
              onMultiSelect={(figs) => {
                setRoundtableFigures(figs);
                setActiveWisdomFigure(null);
                setShowWisdomCouncil(false);
                toast.success(`Council Roundtable: ${figs.length} figures`, {
                  description: figs.map(f => f.name).join(", "),
                });
              }}
              onClose={() => setShowWisdomCouncil(false)}
            />
          </div>
        </div>
      )}
    </AppLayout>
  );
}
